import { MultiList, MultiRange, ReactiveComponent, ReactiveList, SingleList, SingleRange } from "@appbaseio/reactivesearch";
import { SENSOR_IDS } from "../components";
import { Config, ConfigFilter, ConfigFilterOption } from "../types";

export type GeneratePropsFilterGroupOptions = {
  mode: "search" | "instant-search" | "collection";
  config: Config;
  filterGroup: ConfigFilter;

  additionalComponentIds?: Array<string>;
  includeFields?: Array<string>;
  excludeFields?: Array<string>;
};

export const getPropsFilterGroup = (options: GeneratePropsFilterGroupOptions) => {
  return [
    ...options.filterGroup.options.map((filterGroupOption) => getPropsFilterGroupOption(options, filterGroupOption)),
    getPropsResults(options),
  ];
};

export const getPropsFilterGroupOption = (options: GeneratePropsFilterGroupOptions, filterGroupOption: ConfigFilterOption) => {
  let filterProps: Record<string, unknown> = {};

  if (filterGroupOption.displayType === "slider") {
    filterProps = getPropsFilterSlider(filterGroupOption);
  }

  if (filterGroupOption.displayType === "single" || filterGroupOption.displayType === "multi") {
    if (filterGroupOption.displayView === "range") {
      filterProps = getPropsFilterRange(filterGroupOption);
    } else {
      filterProps = getPropsFilterList(filterGroupOption);
    }
  }

  const sharedProps = getPropsShared(options, filterGroupOption);

  return {
    ...sharedProps,
    ...filterProps,
  };
};

export const getPropsResults = (options: GeneratePropsFilterGroupOptions) => {
  const includeFieldsProp = (() => {
    return [
      ...(options.includeFields ?? []),
      "id",
      "title",
      "type",
      "handle",
      "image",
      "variants.id",
      "variants.presentment_prices",
      "callout",
    ];
  })();
  const excludeFieldsProp = (() => {
    return [...(options.excludeFields ?? [])];
  })();

  return {
    size: options.filterGroup.pageSize,
    URLParams: true,
    showLoader: false,
    dataField: "title",
    showResultStats: false,
    react: getPropsReact(options),
    includeFields: includeFieldsProp,
    excludeFields: excludeFieldsProp,
    // scrollTarget: options.scrollTarget,
    componentId: "page", // this sets ?page= in the URL
    scrollOnChange: false, // @todo make this better, it's really janky when enabled
    pagination: options.filterGroup?.paginationType !== "infinite_scroll",
    infiniteScroll: options.filterGroup?.paginationType === "infinite_scroll",
    renderNoResults: () => null, // always use only render, otherwise both are shown
    source: ReactiveList,
  }
}

export const getPropsReact = (options: GeneratePropsFilterGroupOptions, filterGroupOption?: ConfigFilterOption) => {
  const sensorIds = SENSOR_IDS(options.mode, options.config);
  const reactProp = (() => {
    if (filterGroupOption?.settingsShowEmptyValues) {
      return {
        and: sensorIds,
      };
    }

    return {
      and: [
        ...sensorIds,
        ...(options.additionalComponentIds ?? []),
        ...(options.filterGroup?.options.map((filter) => filter.handle) ?? []),
      ].filter((handle) => handle !== filterGroupOption?.handle),
    };
  })();

  return reactProp;
};

export const getPropsShared = (options: GeneratePropsFilterGroupOptions, filterGroupOption: ConfigFilterOption) => {
  const reactProp = getPropsReact(options, filterGroupOption);
  const nestedFieldProp = (() => {
    const nestedFields = ["variants"];
    const topField = filterGroupOption.field.split(".")[0];
    if (nestedFields.includes(topField)) {
      return topField;
    }
    return undefined;
  })();
  const customQueryProp = (() => {
    if (
      !filterGroupOption.settingsHideUnavailable ||
      !filterGroupOption.field.startsWith("variants.")
    )
      return undefined;

    return (value?: string | string[]) => {
      if (!value) return {};

      // reactivesearch sometimes returns string, sometimes string[]
      const valueArray = "string" === typeof value ? [value] : value;

      if (0 === valueArray.length) return {};

      return {
        query: {
          nested: {
            path: "variants",
            query: {
              bool: {
                must: [
                  {
                    match: {
                      "variants.available": "true",
                    },
                  },
                  {
                    terms: {
                      [filterGroupOption.field]: valueArray,
                    },
                  },
                ],
              },
            },
          },
        },
      };
    };
  })();
  const defaultQueryProp = (() => {
    if (!filterGroupOption.field.startsWith("variants.")) {
      return undefined;
    }

    // override the aggregation query to filter the results by the variants.available attribute
    return () => {
      return {
        aggs: {
          reactivesearch_nested: {
            nested: { path: "variants" },
            aggs: {
              reactify_nested_outer: {
                filter: {
                  bool: {
                    minimum_should_match: 1,
                    should: [
                      {
                        match: {
                          "variants.available": "true",
                        },
                      },
                      {
                        match: {
                          "variants.available": filterGroupOption.settingsHideUnavailable
                            ? "true"
                            : "false",
                        },
                      },
                    ],
                  },
                },
                aggs: {
                  [filterGroupOption.field]: {
                    terms: {
                      field: filterGroupOption.field,
                      size: 100,
                      order: { _count: "desc" },
                    },
                    aggs: {
                      parent_docs: { reverse_nested: {} },
                    },
                  },
                },
              },
            },
          },
        },
      };
    };
  })();
  const sourceProp = (() => {
    if (filterGroupOption.displayType === "slider") {
      return ReactiveComponent;
    }
    if (filterGroupOption.displayType === "single") {
      if (filterGroupOption.displayView === "range") {
        return SingleRange;
      }
      return SingleList;
    }
    if (filterGroupOption.displayType === "multi") {
      if (filterGroupOption.displayView === "range") {
        return MultiRange;
      }
      return MultiList;
    }
    
    return;
  })();

  return {
    URLParams: true,
    componentId: filterGroupOption.handle,
    dataField: filterGroupOption.field,
    filterLabel: filterGroupOption.name,
    nestedField: nestedFieldProp,
    customQuery: customQueryProp,
    defaultQuery: defaultQueryProp,
    source: sourceProp,
    react: reactProp,
  }
};

export const getPropsFilterList = (filterGroupOption: ConfigFilterOption) => {
  return {
    size: parseInt(filterGroupOption.displaySize) || undefined,
    queryFormat: filterGroupOption.settingsFilterLogic ?? "or",
    showFilter: filterGroupOption.settingsShowFilter ?? true,
    showLoadMore: filterGroupOption.settingsShowMore ?? false,
    showSearch: filterGroupOption.settingsShowSearch ?? false,
    showRadio: false,
    showCheckbox: false,
  };
};

export const getPropsFilterRange = (filterGroupOption: ConfigFilterOption) => {
  const data = (() => {
    return (
      filterGroupOption.displayRangeOptions?.map((option) => {
        const [label, start, end] = option.split(":");
        return {
          label: label,
          start: start ? parseInt(start) : 0,
          end: end ? parseInt(end) : Number.MAX_SAFE_INTEGER,
        };
      }) ?? []
    );
  })();

  return {
    queryFormat: filterGroupOption.settingsFilterLogic ?? "or",
    showFilter: filterGroupOption.settingsShowFilter ?? true,
    showRadio: false,
    showCheckbox: false,
    data: data,
  };
};

export const getPropsFilterSlider = (filterGroupOption: ConfigFilterOption) => {
  return {}
};